/**
 * Helpers for the Accept Challenge Picks flow.
 * Derives required markets per match from creator's picks, with context for parameterized markets (e.g. Over/Under line).
 */

import type {
  ChallengeOddsSnapshot,
  ChallengeWithStats,
} from '../../../types/challenges';
import type { MarketOdds } from '../../create/types/create.types';
import {
  formatHandicapMarketDisplayName,
  HANDICAP_MARKET_FAMILY,
  MARKET_IDS,
  OU_MARKET_FAMILIES,
} from '../../create/constants/matchDetails.constants';

/** One required market for a match, with optional context for parameterized markets (O/U line, creator value). */
export interface RequiredMarket {
  marketId: number;
  marketName: string;
  /** Creator's pick value (e.g. "Over") so modal can show opposite ("Under") for same line. */
  value?: string;
  /** Parsed line for Over/Under (e.g. "2.5") so invitee sees "Over 2.5" vs "Under 2.5". */
  line?: string;
}

/** matchId -> list of required markets (with context). */
export type RequiredMarketsByMatch = Record<string, RequiredMarket[]>;

/** Match line number at end of market name (e.g. "Over/Under 2.5", "Handicap -1"). "-?" supports negative lines. */
const OU_LINE_REGEX = /(-?\d+\.?\d*)\s*$/;
/** Match line number inside value (e.g. "Over 2.5" -> "2.5"). */
const LINE_FROM_VALUE_REGEX = /(\d+(?:\.\d+)?)/;

/**
 * Parse line from market name for Over/Under-style markets.
 * Examples: "Over/Under 2.5" -> "2.5", "Goals Over/Under 1.5" -> "1.5".
 */
function parseOverUnderLine(marketName: string): string | undefined {
  const match = marketName.match(OU_LINE_REGEX);
  return match ? match[1] : undefined;
}

/** Parse line from pick value (e.g. "Over 2.5" -> "2.5") when market name has no line. */
function parseLineFromValue(value: string): string | undefined {
  const match = String(value ?? '').trim().match(LINE_FROM_VALUE_REGEX);
  return match ? match[1] : undefined;
}

/**
 * Derive required markets per match from the creator's picks.
 * Preserves market context (line, value) for parameterized markets so the invitee UI can show correct options (e.g. "Over 2.5" vs "Under 2.5").
 */
export function getRequiredMarketsPerMatch(challenge: ChallengeWithStats): RequiredMarketsByMatch {
  const picks = challenge.picks ?? [];
  const creatorId = challenge.creatorId;
  const creatorPicks = picks.filter(
    (p): p is typeof p & { matchId: string; marketId: number; marketName: string; value: string } =>
      p.userId === creatorId && !!p.matchId && typeof p.marketId === 'number' && !!p.marketName && p.value != null
  );

  const byMatch: Record<string, Map<number, RequiredMarket>> = {};

  for (const p of creatorPicks) {
    if (!byMatch[p.matchId]) {
      byMatch[p.matchId] = new Map();
    }
    const map = byMatch[p.matchId];
    if (map.has(p.marketId)) continue;

    const line = parseOverUnderLine(p.marketName);
    map.set(p.marketId, {
      marketId: p.marketId,
      marketName: p.marketName,
      value: p.value,
      ...(line && { line }),
    });
  }

  const result: RequiredMarketsByMatch = {};
  for (const [matchId, map] of Object.entries(byMatch)) {
    result[matchId] = Array.from(map.values());
  }

  // Only apply fallback when creator has at least one pick (stale-cache / loading race guard).
  // If no creator picks exist at all, return empty so callers know nothing is required yet.
  if (creatorPicks.length > 0) {
    for (const matchId of (challenge.matchIds ?? [])) {
      if (!result[matchId]) {
        result[matchId] = [{ marketId: MARKET_IDS.MATCH_WINNER, marketName: 'Match Winner (1X2)' }];
      }
    }
    return result;
  }

  // No creator picks visible. On an open challenge the viewer has not taken on yet that is the
  // pick-seal doing its job, not missing data — the server sends the questions without the
  // answers. Fall back to those so the challenge is playable (specs/open-challenges AC-7a).
  //
  // Strictly a FALLBACK: whenever creator picks ARE visible the derivation above wins, because it
  // carries the creator's `value`, which is what lets the modal offer "Under 2.5" opposite their
  // "Over 2.5". The sealed list has no values by design.
  const sealed = challenge.requiredMarkets;
  if (sealed) {
    for (const [matchId, markets] of Object.entries(sealed)) {
      if (Array.isArray(markets) && markets.length > 0) result[matchId] = markets.map((m) => ({ ...m }));
    }
  }

  return result;
}

/** Aligns with OddsMarketSnapshot (name optional on some API payloads). */
type ApiMarket = { id: number; name?: string; values?: Array<{ value: string; odd: number }> };

function getOuFamily(marketId: number): (typeof OU_MARKET_FAMILIES)[number] | null {
  return OU_MARKET_FAMILIES.find((f) => f.ids.includes(marketId)) ?? null;
}

function isHandicapMarketId(marketId: number): boolean {
  return HANDICAP_MARKET_FAMILY.ids.includes(marketId);
}

export function normalizeMarketValue(value: string | null | undefined): string {
  if (value == null || typeof value !== 'string') return '';
  return String(value)
    .toLowerCase()
    .replace(/\s*goals\s*/gi, ' ')
    .replace(/,/g, '.')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getSnapshotMarketsForMatch(
  oddsSnapshot: ChallengeOddsSnapshot | null | undefined,
  matchId: string
): ApiMarket[] {
  if (!oddsSnapshot || !matchId) return [];
  const matchSnapshot = oddsSnapshot[matchId];
  if (!matchSnapshot || !Array.isArray(matchSnapshot.markets)) return [];
  return matchSnapshot.markets;
}

export function get1x2OddsFromMarkets(markets: ApiMarket[]): {
  home: number;
  draw: number;
  away: number;
} | null {
  const market = markets.find((entry) => Number(entry.id) === MARKET_IDS.MATCH_WINNER);
  if (!market?.values?.length) return null;
  const findOdd = (labels: string[]) =>
    market.values?.find((value) => labels.includes(String(value.value ?? '').toLowerCase().trim()))?.odd;

  const home = findOdd(['home', '1']);
  const draw = findOdd(['draw', 'x']);
  const away = findOdd(['away', '2']);
  if (typeof home !== 'number' || typeof draw !== 'number' || typeof away !== 'number') {
    return null;
  }
  return { home, draw, away };
}

/** True when odds.markets includes a complete Match Winner (Home/Draw/Away) with numeric odds. */
export function hasFullMatchWinner1x2InMarkets(markets: ApiMarket[] | null | undefined): boolean {
  if (!Array.isArray(markets) || markets.length === 0) return false;
  return get1x2OddsFromMarkets(markets) != null;
}

/**
 * Whether to render the Match Winner (1X2) bet row on challenge / Arena cards.
 * Uses frozen challenge odds when available; always shows if creator required 1x2 or either side has a stored 1x2 pick.
 */
export function shouldShowMatchWinner1x2BetRow(params: {
  snapshotMarkets: ApiMarket[];
  challengeHasOddsSnapshot: boolean;
  hasRequiredMatchWinner: boolean;
  hasUserStored1x2: boolean;
  hasOpponentStored1x2: boolean;
}): boolean {
  const {
    snapshotMarkets,
    challengeHasOddsSnapshot,
    hasRequiredMatchWinner,
    hasUserStored1x2,
    hasOpponentStored1x2,
  } = params;

  if (hasRequiredMatchWinner || hasUserStored1x2 || hasOpponentStored1x2) {
    return true;
  }

  if (!challengeHasOddsSnapshot) {
    return false;
  }

  if (snapshotMarkets.length === 0) {
    return false;
  }

  return hasFullMatchWinner1x2InMarkets(snapshotMarkets);
}

/**
 * Build MarketOdds[] for non-1X2 required markets. Merges O/U by family.
 * Used by AcceptChallengePicksModal and ChallengeCardMatches for consistent market display.
 */
export function buildMarketsForMatch(
  otherMarkets: RequiredMarket[],
  apiMarkets: ApiMarket[]
): MarketOdds[] {
  const result: MarketOdds[] = [];
  const ouByFamily = new Map<(typeof OU_MARKET_FAMILIES)[number], RequiredMarket[]>();
  const nonOu: RequiredMarket[] = [];

  const handicapRms: RequiredMarket[] = [];
  for (const rm of otherMarkets) {
    const family = getOuFamily(rm.marketId);
    if (family && rm.line) {
      const list = ouByFamily.get(family) ?? [];
      list.push(rm);
      ouByFamily.set(family, list);
    } else if (isHandicapMarketId(rm.marketId)) {
      handicapRms.push(rm);
    } else {
      nonOu.push(rm);
    }
  }

  for (const family of OU_MARKET_FAMILIES) {
    const list = ouByFamily.get(family);
    if (!list?.length) continue;
    const sorted = [...list].sort(
      (a, b) => (parseFloat(a.line ?? '0') || 0) - (parseFloat(b.line ?? '0') || 0)
    );
    const values: Array<{ value: string; odd: number; marketId: number }> = [];
    for (const rm of sorted) {
      const line = rm.line!;
      const fromApi = apiMarkets.find((m) => m.id === rm.marketId);
      const apiVals = fromApi?.values ?? [];
      if (apiVals.length >= 2) {
        const over = apiVals.find((v) => String(v.value).toLowerCase().includes('over'));
        const under = apiVals.find((v) => String(v.value).toLowerCase().includes('under'));
        if (over) values.push({ value: `Over ${line}`, odd: over.odd, marketId: rm.marketId });
        if (under) values.push({ value: `Under ${line}`, odd: under.odd, marketId: rm.marketId });
      } else {
        values.push({ value: `Over ${line}`, odd: 1, marketId: rm.marketId });
        values.push({ value: `Under ${line}`, odd: 1, marketId: rm.marketId });
      }
    }
    result.push({
      id: family.ids[0],
      name: family.baseName,
      values,
    });
  }

  if (handicapRms.length > 0) {
    const sorted = [...handicapRms].sort(
      (a, b) =>
        (parseFloat(parseOverUnderLine(a.marketName) ?? '0') || 0) -
        (parseFloat(parseOverUnderLine(b.marketName) ?? '0') || 0)
    );
    const values: Array<{ value: string; odd: number; marketId: number }> = [];
    for (const rm of sorted) {
      const fromApi = apiMarkets.find((m) => m.id === rm.marketId);
      const apiVals = fromApi?.values ?? [];
      if (apiVals.length >= 1) {
        for (const av of apiVals) {
          values.push({
            value: String(av.value ?? '').trim(),
            odd: Number(av.odd) || 0,
            marketId: rm.marketId,
          });
        }
      } else {
        const fallbackValues = [rm.value ?? 'Home', 'Draw', 'Away'];
        for (const fallbackValue of fallbackValues) {
          values.push({
            value: fallbackValue,
            odd: 1,
            marketId: rm.marketId,
          });
        }
      }
    }
    const handicapName =
      handicapRms.length === 1
        ? formatHandicapMarketDisplayName(
            handicapRms[0].marketId,
            HANDICAP_MARKET_FAMILY.baseName,
            handicapRms[0].value
          )
        : HANDICAP_MARKET_FAMILY.baseName;
    result.push({
      id: HANDICAP_MARKET_FAMILY.ids[0],
      name: handicapName,
      values,
    });
  }

  for (const rm of nonOu) {
    const fromApi = apiMarkets.find((m) => m.id === rm.marketId);
    let values: Array<{ value: string; odd: number; marketId?: number }> = fromApi?.values ?? [];
    const ouFamily = getOuFamily(rm.marketId);
    if (ouFamily && values.length > 0) {
      const line = parseLineFromValue(rm.value ?? '');
      if (line) {
        values = values.filter((v) => {
          const val = String(v.value ?? '').toLowerCase();
          return val.includes(`over ${line}`) || val.includes(`under ${line}`);
        });
      }
    }
    if (values.length === 0) {
      if (rm.marketName.toLowerCase().includes('both teams')) {
        values = [
          { value: 'Yes', odd: 1 },
          { value: 'No', odd: 1 },
        ];
      } else if (
        rm.marketId === MARKET_IDS.CLEAN_SHEET_HOME ||
        rm.marketId === MARKET_IDS.CLEAN_SHEET_AWAY
      ) {
        values = [
          { value: 'Yes', odd: 1 },
          { value: 'No', odd: 1 },
        ];
      } else if (rm.value) {
        values = [
          {
            value: rm.value,
            odd: 1,
          },
        ];
      }
    }
    if (values.length > 0) {
      result.push({ id: rm.marketId, name: rm.marketName, values });
    }
  }
  return result;
}
